import { Klussen } from "@personeel/components/Werklijnen/Personeelsdiensten/Klussen/Klussen";
import { useKlussenStore } from "@personeel/stores/KlussenStore.ts";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import gridStyles from "../WerklijnenContainer/WerklijnenContainer.module.css";
import dienstStyles from "./Personeelsdienst/Personeelsdienst.module.css";
import labelStyles from "./PersoneelsdienstLabel/PersoneelsdienstLabel.module.css";

interface INietToegewezenKlussen {
  scale: number;
}

export function NietToegewezenKlussen({ scale }: Readonly<INietToegewezenKlussen>) {
  const klussen = useKlussenStore((state) => state.klussen);
  const timeLineBegin = useSelectedTimeStore((state) => state.scenarioInterval?.start);
  const timeLineEnd = useSelectedTimeStore((state) => state.scenarioInterval?.end);
  const selectedTime = useSelectedTimeStore((state) => state.selectedTime);
  const setSelectedTime = useSelectedTimeStore((state) => state.setDateTime);
  const nietToegewezenKlussen = klussen.filter((klus) => !klus.dienst || klus.dienst.trim() === "");

  const centerOnUnassignedKlus = (direction: "left" | "right") => {
    if (!selectedTime || !timeLineBegin || !timeLineEnd) {
      return;
    }

    const klussenByCenterTime = nietToegewezenKlussen
      .filter(
        (klus) =>
          klus.eindDatumtijd.toMillis() > timeLineBegin.toMillis() &&
          klus.beginDatumtijd.toMillis() < timeLineEnd.toMillis(),
      )
      .map((klus) => {
        return {
          center: klus.beginDatumtijd.plus({
            milliseconds: klus.eindDatumtijd.diff(klus.beginDatumtijd).as("milliseconds") / 2,
          }),
        };
      })
      .sort((a, b) => a.center.toMillis() - b.center.toMillis());

    if (klussenByCenterTime.length === 0) {
      return;
    }

    const epsilonMilliseconds = 3_000; //deze is nodig wanneer het klusmidden tussen twee 6-seconden stappen valt.
    const currentTimeMilliseconds = selectedTime.toMillis();
    const targetKlus =
      direction === "right"
        ? klussenByCenterTime.find(
            ({ center }) => center.toMillis() > currentTimeMilliseconds + epsilonMilliseconds,
          )
        : [...klussenByCenterTime]
            .reverse()
            .find(
              ({ center }) => center.toMillis() < currentTimeMilliseconds - epsilonMilliseconds,
            );

    if (!targetKlus) {
      return;
    }

    setSelectedTime(targetKlus.center);
  };

  if (nietToegewezenKlussen.length === 0) {
    return null;
  }

  return (
    <>
      <div className={gridStyles["grid-item"] + " " + gridStyles["grid-sidebar"]}>
        <div
          data-testid="niettoegewezenklussen-klussen"
          className={labelStyles["personeeldienst-label"]}
        >
          <tractie-tag color="red" appearance="subtle" size="m">
            <div className="flex items-center justify-center gap-0.5">
              <tractie-icon name="question" size="xs" />
              {nietToegewezenKlussen.length}
            </div>
          </tractie-tag>
        </div>
      </div>
      <div className={gridStyles["grid-item"]} id="niet-toegewezen-dienst">
        <div
          id="niet-toegewezen-duty-line"
          className={`${dienstStyles["c-duty-task-line"]} ${dienstStyles["c-duty-task-line--solid"]}`}
          data-duty-group-id="niet-toegewezen-dienst"
        >
          <div
            className={dienstStyles["c-duty-drop-zone"]}
            data-unassigned-drop="true"
            style={{ left: 0, width: "100%" }}
          />
          <Klussen klussen={nietToegewezenKlussen} scale={scale} />
          <div className={dienstStyles["c-duty-floating-navigate-container"]}>
            <button
              type="button"
              aria-label="Ga naar vorige oningedeelde klus"
              className={`${dienstStyles["c-duty-floating-navigate-button"]} ${dienstStyles["c-duty-floating-navigate-button--left"]}`}
              onClick={() => centerOnUnassignedKlus("left")}
            >
              <tractie-icon name="chevron-double-left" />
            </button>
            <button
              type="button"
              aria-label="Ga naar volgende oningedeelde klus"
              className={`${dienstStyles["c-duty-floating-navigate-button"]} ${dienstStyles["c-duty-floating-navigate-button--right"]}`}
              onClick={() => centerOnUnassignedKlus("right")}
            >
              <tractie-icon name="chevron-double-right" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
